import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { ALL_ROLES, type RoleSlug, useCreateUser } from "~/lib/users";

import { CopyButton } from "./CopyButton";

/**
 * "Invite user" dialog.
 *
 * Fields: email + multi-select roles. Password is auto-generated server-side
 * and surfaced once in the success state — admin shares it with the new user
 * via whatever out-of-band channel they prefer (Slack DM, password manager).
 */
export function InviteUserDialog(): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [roles, setRoles] = useState<RoleSlug[]>(["operator"]);
    const [error, setError] = useState<string | null>(null);
    const [createdPassword, setCreatedPassword] = useState<{ email: string; password: string } | null>(null);

    const create = useCreateUser();

    function reset(): void {
        setEmail("");
        setRoles(["operator"]);
        setError(null);
        setCreatedPassword(null);
        create.reset();
    }

    function toggleRole(role: RoleSlug): void {
        setRoles((current) =>
            current.includes(role) ? current.filter((r) => r !== role) : [...current, role],
        );
    }

    async function onSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setError(null);
        const trimmed = email.trim();
        if (!trimmed) {
            setError("Email is required.");
            return;
        }
        if (roles.length === 0) {
            setError("At least one role required.");
            return;
        }
        try {
            const res = await create.mutateAsync({ email: trimmed, roles });
            if (res.temporaryPassword) {
                setCreatedPassword({ email: res.user.email, password: res.temporaryPassword });
            } else {
                toast.success(`Invited ${res.user.email}`);
                setOpen(false);
                reset();
            }
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to create user.");
        }
    }

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
            }}
        >
            <Dialog.Trigger asChild>
                <button type="button" className="btn btn--primary press-crush">
                    <Plus size={14} />
                    <span>Invite user</span>
                </button>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                <Dialog.Content
                    className="dialog-content enter-up"
                    style={{ padding: "var(--space-6)" }}
                >
                    <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                        <Dialog.Title className="type-h2">
                            {createdPassword ? "User created" : "Invite user"}
                        </Dialog.Title>
                        <Dialog.Close asChild>
                            <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                <X size={14} />
                            </button>
                        </Dialog.Close>
                    </div>

                    {createdPassword ? (
                        <div className="stack" style={{ marginTop: "var(--space-4)" }}>
                            <p className="text-text-muted text-13">
                                Share this temporary password with{" "}
                                <strong className="text-text">{createdPassword.email}</strong>.
                                It is shown once and cannot be recovered. They can change it after first sign-in.
                            </p>
                            <div
                                className="surface"
                                style={{
                                    padding: "var(--space-3)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "var(--space-3)",
                                    fontFamily: "var(--font-family-mono)",
                                }}
                            >
                                <span style={{ flex: 1, wordBreak: "break-all" }}>{createdPassword.password}</span>
                                <CopyButton value={createdPassword.password} />
                            </div>
                            <button
                                type="button"
                                className="btn btn--primary press-crush"
                                onClick={() => {
                                    setOpen(false);
                                    reset();
                                }}
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                        <Dialog.Description asChild>
                            <form onSubmit={onSubmit} className="stack" style={{ marginTop: "var(--space-4)" }}>
                                <label className="stack-xs">
                                    <span className="text-12 text-text-muted">Email</span>
                                    <input
                                        type="email"
                                        autoFocus
                                        required
                                        className="input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="newuser@dataflowx.com"
                                    />
                                </label>

                                <div className="stack-xs">
                                    <span className="text-12 text-text-muted">Roles</span>
                                    <div className="cluster-xs">
                                        {ALL_ROLES.map((role) => {
                                            const active = roles.includes(role);
                                            return (
                                                <button
                                                    key={role}
                                                    type="button"
                                                    onClick={() => toggleRole(role)}
                                                    className={`chip ${active ? "chip--accent" : ""}`}
                                                    style={{
                                                        cursor: "pointer",
                                                        height: 26,
                                                        opacity: active ? 1 : 0.6,
                                                    }}
                                                    aria-pressed={active}
                                                >
                                                    {role}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {error && (
                                    <div className="chip chip--danger" style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }} role="alert">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="btn btn--primary press-crush"
                                    disabled={create.isPending}
                                >
                                    {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                    <span>{create.isPending ? "Creating…" : "Create user"}</span>
                                </button>
                            </form>
                        </Dialog.Description>
                    )}
                </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
